import React, { useState } from 'react';
import { X, User, Mail, Phone, Shield, Building2, MapPin, Save, Eye, EyeOff, Plus, GraduationCap } from 'lucide-react';
import { UserRole, UnitType } from '../../types';
import { allUniversities } from '../../utils/consultantMatcher';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userData: any) => void;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ isOpen, onClose, onSave }) => {
  const { user: currentUser } = useAuth();
  const [branches, setBranches] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'unit' as UserRole,
    branchId: '',
    unitId: '',
    unitType: 'education_consultant' as UnitType,
    password: '',
    confirmPassword: '',
    permissions: [] as string[],
    responsibleUniversities: [] as string[]
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [newUniversity, setNewUniversity] = useState('');

  // Fetch branches and units from database
  React.useEffect(() => {
    if (isOpen) {
      fetchBranchesAndUnits();
    }
  }, [isOpen]);

  const fetchBranchesAndUnits = async () => {
    try {
      setLoading(true);

      if (!supabase) {
        console.log('Supabase bağlantısı henüz kurulmadı');
        return;
      }

      // Fetch branches
      const { data: branchesData, error: branchesError } = await supabase
        .from('branches')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (branchesError) {
        console.error('Şubeler yüklenirken hata:', branchesError);
      } else {
        setBranches(branchesData || []);
      }

      // Fetch units
      const { data: unitsData, error: unitsError } = await supabase
        .from('units')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (unitsError) {
        console.error('Birimler yüklenirken hata:', unitsError);
      } else {
        setUnits(unitsData || []);
      }
    } catch (error) {
      console.error('Veriler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUnitTypeName = (type: string): string => {
    switch (type) {
      case 'education_consultant': return 'Eğitim Danışmanı';
      case 'visa_consultant': return 'Vize Danışmanı';
      case 'marketing': return 'Pazarlama';
      case 'accounting': return 'Muhasebe';
      case 'customer_service': return 'Müşteri Hizmetleri';
      case 'sales': return 'Satış';
      case 'testprep': return 'Test Hazırlık';
      case 'teknik_destek': return 'Teknik Destek';
      default: return type;
    }
  };

  const filteredUnits = units.filter(unit =>
    !formData.branchId || unit.branch_id === formData.branchId
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const newData = { ...prev, [name]: value };

      // If unitId changes, automatically set the unitType
      if (name === 'unitId') {
        const selectedUnit = units.find(u => u.id === value);
        if (selectedUnit) {
          newData.unitType = selectedUnit.type as UnitType;
        }
      }

      return newData;
    });
  };

  const handlePermissionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      permissions: checked
        ? [...prev.permissions, value]
        : prev.permissions.filter(p => p !== value)
    }));
  };

  const addUniversity = () => {
    if (newUniversity && !formData.responsibleUniversities.includes(newUniversity)) {
      setFormData(prev => ({
        ...prev,
        responsibleUniversities: [...prev.responsibleUniversities, newUniversity]
      }));
      setNewUniversity('');
    }
  };

  const removeUniversity = (university: string) => {
    setFormData(prev => ({
      ...prev,
      responsibleUniversities: prev.responsibleUniversities.filter(u => u !== university)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      alert('Şifreler eşleşmiyor!');
      return;
    }

    if (formData.password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır!');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      // Check environment variables
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      console.log('Environment check:', {
        supabaseUrl: supabaseUrl ? 'Set' : 'Missing',
        supabaseAnonKey: supabaseAnonKey ? 'Set' : 'Missing'
      });

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Supabase ortam değişkenleri eksik. Lütfen .env dosyasını kontrol edin.');
      }

      // Check if we have Supabase connection
      if (!supabase) {
        throw new Error('Supabase bağlantısı bulunamadı');
      }

      // Get current session
      const { data: session, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        console.error('Session error:', sessionError);
        throw new Error(`Oturum hatası: ${sessionError.message}`);
      }

      if (!session.session) {
        throw new Error('Oturum bulunamadı. Lütfen tekrar giriş yapın.');
      }

      // Construct the Edge Function URL
      const functionUrl = `${supabaseUrl}/functions/v1/create-user`;
      console.log('Calling Edge Function:', functionUrl);

      // Prepare request data
      const requestData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone || null,
        role: formData.role,
        branchId: formData.branchId || null,
        unitId: formData.unitId || null,
        unitType: formData.role === 'unit' ? formData.unitType : null,
        permissions: formData.permissions,
        responsibleUniversities: formData.responsibleUniversities
      };

      console.log('Request data:', requestData);

      // Call the Edge Function with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.session.access_token}`,
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey
        },
        body: JSON.stringify(requestData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        let errorText;
        try {
          errorText = await response.text();
        } catch (e) {
          errorText = `HTTP ${response.status} - Response could not be read`;
        }
        console.error('Edge Function error:', response.status, errorText);
        throw new Error(`Edge Function hatası (${response.status}): ${errorText}`);
      }

      let result;
      try {
        result = await response.json();
      } catch (e) {
        console.error('JSON parse error:', e);
        throw new Error('Sunucu yanıtı okunamadı. Lütfen tekrar deneyin.');
      }

      console.log('Edge Function response:', result);

      if (!result.success) {
        throw new Error(result.error || 'Kullanıcı oluşturulurken hata oluştu');
      }

      // Create user object for the parent component
      const newUser = {
        id: result.user.id,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        branchId: formData.branchId || undefined,
        unitId: formData.unitId || undefined,
        unitType: formData.unitType,
        isActive: true,
        createdAt: new Date(),
        permissions: formData.permissions,
        responsibleUniversities: formData.responsibleUniversities
      };

      onSave(newUser);

      // Form temizle
      setFormData({
        name: '',
        email: '',
        phone: '',
        role: 'unit',
        branchId: '',
        unitId: '',
        unitType: 'education_consultant',
        password: '',
        confirmPassword: '',
        permissions: [],
        responsibleUniversities: []
      });

      alert(`${formData.name} başarıyla oluşturuldu! Kullanıcı artık ${formData.email} ve belirlediğiniz şifre ile giriş yapabilir.`);

    } catch (error: any) {
      console.error('Kullanıcı oluşturma hatası:', error);

      // Fallback for existing emails: create a secondary profile entry in profiles table
      const isAlreadyRegistered = error?.message?.toLowerCase().includes('already registered') ||
        error?.message?.toLowerCase().includes('already exists') ||
        error?.message?.toLowerCase().includes('user_already_exists');

      if (isAlreadyRegistered) {
        try {
          console.log('Existing email detected. Creating secondary department profile in database...');
          const newProfileId = crypto.randomUUID();
          const { error: profileInsError } = await supabase
            .from('profiles')
            .insert([{
              id: newProfileId,
              name: formData.name,
              email: formData.email,
              phone: formData.phone || null,
              role: formData.role,
              branch_id: formData.branchId || null,
              unit_id: formData.unitId || null,
              unit_type: formData.role === 'unit' ? formData.unitType : null,
              is_active: true,
              permissions: formData.permissions || [],
              responsible_universities: formData.responsibleUniversities || []
            }]);

          if (profileInsError) throw profileInsError;

          alert(`${formData.name} için "${formData.email}" e-postasına yeni departman hesabı başarıyla eklendi!\nKullanıcı giriş yaptıktan sonra sol menüdeki "Hesap Değiştir" alanından bu departmana tek tıkla geçiş yapabilir.`);

          onSave({
            id: newProfileId,
            name: formData.name,
            email: formData.email,
            role: formData.role,
            unitType: formData.unitType
          });

          setFormData({
            name: '',
            email: '',
            phone: '',
            role: 'unit',
            branchId: '',
            unitId: '',
            unitType: 'education_consultant',
            password: '',
            confirmPassword: '',
            permissions: [],
            responsibleUniversities: []
          });
          onClose();
          return;
        } catch (fallbackErr: any) {
          console.error('Secondary profile creation error:', fallbackErr);
          setError(fallbackErr.message || 'Departman hesabı eklenirken hata oluştu');
          return;
        }
      }

      setError(error.message || 'Kullanıcı oluşturulurken bir hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  const getRolePermissions = () => {
    switch (formData.role) {
      case 'admin':
        return [
          { id: 'all', label: 'Tüm Yetkiler' }
        ];
      case 'branch':
        return [
          { id: 'view_students', label: 'Öğrencileri Görüntüleme' },
          { id: 'edit_students', label: 'Öğrenci Düzenleme' },
          { id: 'view_reports', label: 'Raporları Görüntüleme' },
          { id: 'manage_units', label: 'Birimleri Yönetme' }
        ];
      case 'unit':
        if (formData.unitType === 'education_consultant') {
          return [
            { id: 'view_students', label: 'Öğrencileri Görüntüleme' },
            { id: 'edit_students', label: 'Öğrenci Düzenleme' },
            { id: 'add_notes', label: 'Not Ekleme' },
            { id: 'schedule_meetings', label: 'Görüşme Planlama' }
          ];
        } else if (formData.unitType === 'visa_consultant') {
          return [
            { id: 'view_students', label: 'Öğrencileri Görüntüleme' },
            { id: 'edit_students', label: 'Öğrenci Düzenleme' },
            { id: 'manage_documents', label: 'Belge Yönetimi' },
            { id: 'track_applications', label: 'Başvuru Takibi' }
          ];
        } else if (formData.unitType === 'marketing') {
          return [
            { id: 'view_leads', label: 'Lead Görüntüleme' },
            { id: 'manage_campaigns', label: 'Kampanya Yönetimi' },
            { id: 'view_analytics', label: 'Analitik Görüntüleme' }
          ];
        } else if (formData.unitType === 'accounting') {
          return [
            { id: 'view_payments', label: 'Ödemeleri Görüntüleme' },
            { id: 'manage_invoices', label: 'Fatura Yönetimi' },
            { id: 'view_financial_reports', label: 'Mali Raporları Görüntüleme' }
          ];
        } else if (formData.unitType === 'testprep') {
          return [
            { id: 'schedule_meetings', label: 'Görüşme Planlama' }
          ];
        } else if (formData.unitType === 'teknik_destek') {
          return [
            { id: 'view_all_tickets', label: 'Tüm Talepleri Görme' },
            { id: 'manage_tickets', label: 'Talepleri Yönetme' },
            { id: 'view_logs', label: 'Sistem Loglarını Görme' }
          ];
        } else {
          return [
            { id: 'view_students', label: 'Öğrencileri Görüntüleme' }
          ];
        }
      case 'student':
        return [
          { id: 'view_own_profile', label: 'Kendi Profilini Görüntüleme' },
          { id: 'upload_documents', label: 'Belge Yükleme' },
          { id: 'view_applications', label: 'Başvuruları Görüntüleme' },
          { id: 'message_consultants', label: 'Danışmanlarla Mesajlaşma' }
        ];
      default:
        return [];
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Yeni Kullanıcı Ekle</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Kullanıcı Bilgileri</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ad Soyad <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Örn: Fatma Özkan"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Örn: fatma.ozkan@pisaeducazione.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Telefon
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Örn: +90 555 123 4567"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rol <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="admin">Sistem Yöneticisi</option>
                    <option value="branch">Şube Yöneticisi</option>
                    <option value="unit">Birim Çalışanı</option>
                    <option value="student">Öğrenci</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Role Specific Information */}
          {(formData.role === 'branch' || formData.role === 'unit') && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Organizasyon Bilgileri</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {formData.role === 'branch' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Şube <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <select
                        name="branchId"
                        value={formData.branchId}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Şube Seçin</option>
                        {filteredUnits.map(unit => (
                          <option key={unit.id} value={unit.id}>{unit.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                {formData.role === 'unit' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Şube <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <select
                          name="branchId"
                          value={formData.branchId}
                          onChange={handleChange}
                          required
                          className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Şube Seçin</option>
                          {branches.map(branch => (
                            <option key={branch.id} value={branch.id}>{branch.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Birim <span className="text-gray-500 text-xs">(Opsiyonel)</span>
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <select
                          name="unitId"
                          value={formData.unitId}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Birim Seçin</option>
                          {filteredUnits.map(unit => (
                            <option key={unit.id} value={unit.id}>{unit.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Birim Türü <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <select
                          name="unitType"
                          value={formData.unitType}
                          onChange={handleChange}
                          required
                          className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="education_consultant">Eğitim Danışmanı</option>
                          <option value="visa_consultant">Vize Danışmanı</option>
                          <option value="sales">Satış</option>
                          <option value="marketing">Pazarlama</option>
                          <option value="accounting">Muhasebe</option>
                          <option value="customer_service">Müşteri Hizmetleri</option>
                          <option value="testprep">Test Hazırlık</option>
                          <option value="teknik_destek">Teknik Destek</option>
                        </select>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Responsible Universities for Education Consultants */}
          {formData.role === 'unit' && formData.unitType === 'education_consultant' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Sorumlu Üniversiteler</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.responsibleUniversities.map((university) => (
                  <span
                    key={university}
                    className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 dark:bg-blue-600/20 dark:text-blue-300 text-sm rounded-full border border-blue-200 dark:border-blue-600/30"
                  >
                    <span>{university}</span>
                    <button
                      type="button"
                      onClick={() => removeUniversity(university)}
                      className="hover:text-blue-600 dark:hover:text-blue-200"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex space-x-2">
                <select
                  value={newUniversity}
                  onChange={(e) => setNewUniversity(e.target.value)}
                  className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">İtalyan üniversitesi seçin...</option>
                  {allUniversities
                    .filter(uni => !formData.responsibleUniversities.includes(uni))
                    .map(uni => (
                      <option key={uni} value={uni}>{uni}</option>
                    ))}
                </select>
                <button
                  type="button"
                  onClick={addUniversity}
                  disabled={!newUniversity}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center space-x-1"
                >
                  <Plus className="w-4 h-4" />
                  <span>Ekle</span>
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                {/* Şehir bazında hızlı seçim */}
              </p>
            </div>
          )}


          {/* Password */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Şifre</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Şifre <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="w-full pr-10 px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Şifre girin"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Şifre (Tekrar) <span className="text-red-500">*</span>
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Şifreyi tekrar girin"
                />
              </div>
            </div>
          </div>

          {/* Permissions */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Yetkiler</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {getRolePermissions().map(permission => (
                <div key={permission.id} className="flex items-center">
                  <input
                    type="checkbox"
                    id={permission.id}
                    value={permission.id}
                    checked={formData.permissions.includes(permission.id)}
                    onChange={handlePermissionChange}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label htmlFor={permission.id} className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                    {permission.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-all ${isSaving
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                : 'bg-blue-600 hover:bg-blue-700 text-white hover:scale-105 hover:shadow-lg hover:shadow-blue-600/25'
                }`}
            >
              <Save className={`w-4 h-4 ${isSaving ? 'animate-pulse' : ''}`} />
              <span>{isSaving ? 'Kaydediliyor...' : 'Kaydet'}</span>
            </button>
          </div>
        </form>
      </div >
    </div >
  );
};

export default AddUserModal;